import React, { useMemo } from 'react'
import { Col, Grid, Icon, NetworkIcon, Tooltip, Typography } from 'gu-ui-kit'
import { useDispatch, useSelector } from 'react-redux'
import { isNumber } from 'lodash/lang'
import PropTypes from 'prop-types'
import { formatByLocale } from 'utils/formatMoney'
import { ChartPreview } from '../ChartPreview'
import { CardSettings } from '../CardSettings'
import styles from 'modules/Statistics/Pulse/components/Card/Card.module.scss'
import clsx from 'clsx'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'

export const Card = ({
  className = '',
  isActive = false,
  id = '',
  value = 0,
  title = '',
  tooltip = '',
  data = [],
}) => {
  const dispatch = useDispatch()
  const breakpoints = useBreakpoint()
  const { measure } = useSelector((state) => state['statistics/pulse'])

  const handleClick = () => {
    dispatch({ type: 'PULSE/TOGGLE_METRIC_ACTIVE', payload: { id } })
  }

  const iconName = useMemo(() => {
    if (id.includes('google_analytics_')) {
      return 'google-analytics'
    }

    if (id.includes('common_metrics')) {
      return 'getuniq'
    }

    return 'yandex'
  }, [id])

  const diff = useMemo(() => {
    const dataWithValue = data.map((item) => (isNumber(item) ? item : 0))
    const dataWithValueLength = dataWithValue.length
    if (dataWithValueLength) {
      const firstData = dataWithValue[0]
      const lastData = dataWithValue[dataWithValueLength - 1]
      const diff = lastData - firstData
      const hasMeasure = measure[id] || ''

      const valueWithMeasure = `${formatByLocale(value, null, {
        maximumFractionDigits: 2,
      })} ${hasMeasure}`
      const numberWithMeasure = `${formatByLocale(diff, null, {
        maximumFractionDigits: 2,
      })} ${hasMeasure}`
      const valueLength = toString(value).length
      const percent = parseInt((lastData * 100) / firstData - 100)

      return {
        value: valueWithMeasure,
        valueLength,
        number: diff,
        numberWithMeasure,
        percent: diff !== 0 ? percent : 0,
      }
    }
    return null
  }, [data])

  const getSuffix = (number) =>
    number > 0 ? 'up' : number < 0 ? 'down' : 'zero'

  const handleRemove = () => {
    dispatch({ type: 'PULSE/REMOVE_USER_METRIC', payload: { id } })
  }

  const headerSize = useMemo(() => {
    switch (true) {
      case breakpoints.sm:
        return 'Body-Accent-40'
      case breakpoints.fb:
        return 'Body-Accent-60'
      default:
        return 'Body-Accent-80'
    }
  }, [breakpoints])

  const gapSize = useMemo(() => {
    switch (true) {
      case breakpoints.sm:
        return 12
      case breakpoints.fb:
        return 12
      default:
        return 16
    }
  }, [breakpoints])

  return (
    <Col
      s={12}
      sm={6}
      fb={6}
      md={6}
      lg={4}
      xl={3}
      id={id}
      className={clsx(styles['card'], className, {
        [styles['card_active']]: isActive,
      })}
      onClick={handleClick}
    >
      <Grid
        direction={'column'}
        justifyContent={'space-between'}
        className={styles['card__content']}
        wrap={false}
        gap={gapSize}
      >
        <Grid direction={'column'} gap={gapSize}>
          <Grid
            direction={'row'}
            justifyContent={'space-between'}
            alignItems={'center'}
            wrap={false}
            fluid={false}
            gap={4}
          >
            <Grid
              className={styles['card__label']}
              wrap={false}
              alignItems={'center'}
              gap={16}
            >
              <Tooltip title={tooltip} placement="top" leaveDelay={250} arrow>
                <div className={styles['card__label-icon-wrapper']}>
                  <NetworkIcon
                    className={styles['card__label-icon']}
                    name={iconName}
                    size={'s'}
                    rounded={true}
                  />
                </div>
              </Tooltip>
              {title}
            </Grid>
            <CardSettings
              className={styles['card__settings']}
              id={id}
              isActive={isActive}
              handleRemove={handleRemove}
            />
          </Grid>
          <Typography
            variant={headerSize}
            className={clsx(styles['card__heading'], {
              [styles['card__heading_zero']]: value === 0,
            })}
            dangerouslySetInnerHTML={{
              __html: value !== 0 ? diff.value : 0,
            }}
          />

          {value !== 0 && (
            <Typography
              variant={'Body-15'}
              className={clsx(
                styles['card__status'],
                styles['card__status_' + getSuffix(diff.number)],
              )}
            >
              <Icon
                className={styles['card__status-icon']}
                name={`arrow-line-${getSuffix(diff.number)}`}
                size="s"
                color={'inherit'}
              />

              <div className={styles['card__status-text']}>
                <span
                  dangerouslySetInnerHTML={{
                    __html: diff.numberWithMeasure,
                  }}
                />{' '}
                {!isNaN(diff.percent) && (
                  <span className={styles['card__status-text-sub']}>
                    ({diff.percent} %)
                  </span>
                )}
              </div>
            </Typography>
          )}
        </Grid>
        <ChartPreview diff={diff} isActive={isActive} data={data} />
      </Grid>
    </Col>
  )
}

Card.defaultProps = {
  className: '',
  isActive: false,
  id: '',
  value: 0,
  title: '',
  tooltip: '',
  data: [],
}

Card.propTypes = {
  className: PropTypes.string,
  isActive: PropTypes.bool,
  id: PropTypes.string,
  value: PropTypes.number,
  title: PropTypes.string,
  tooltip: PropTypes.string,
  data: PropTypes.array,
}
